import React, { useCallback, useEffect, useState } from 'react';
import Select from '../../components/Select';
import { api } from '../../api';
import { Panel, Empty, plural, fmtNum, fmtRub } from './charts';
import ExpenseForm from './ExpenseForm';

// «Ссылки и QR» модуля «Источники заказов» (13.10.2026): основные ссылки мест (Инстаграм,
// карты, канал, визитка) создаются сами; свои ссылки — для листовок, блогеров, акций, со сроком.
// Ссылки собирает сервер из имени ботов и адреса сайта магазина — здесь только «Копировать».
// 13.10 (вечер): места — раскрывающимся списком; внутри: «Куда ведёт» (сайт / Telegram / Max) →
// ссылка или QR → шаги «Куда вставить» (владелец: карточками читалось тяжело).
// Своя строка может быть без ссылки (link_enabled=false): блогер, радио, подарки — только бюджет.
// 27.10: в карточках «Реклама в Telegram / Max» — ссылки под отдельные объявления и объявления из Директа:
// Директ не отдаёт ссылку объявления в каналах, поэтому какая она — владелец выбирает здесь.

const ENTRY = { site: 'Сайт', tg: 'Telegram', max: 'Max' };
const ENTRY_TO = { site: 'На сайт магазина', tg: 'В Telegram-бота', max: 'В Max-бота' };

async function copyText(text, showToast) {
  try {
    await navigator.clipboard.writeText(text);
    showToast?.('Скопировано');
  } catch (e) {
    showToast?.('Не скопировалось — выделите и скопируйте вручную', 'error');
  }
}

function entriesOf(link) {
  const first = link.first && link.urls[link.first] ? link.first : 'site';
  return [first, ...['site', 'tg', 'max'].filter(k => k !== first)].filter(k => link.urls[k]);
}

const per = (n) => plural(n || 0, 'заказ', 'заказа', 'заказов');

function Activity({ link }) {
  const w = link.week || {};
  const t = link.total || {};
  if (w.people) return <span className="an-mk-act">{plural(w.people, 'переход', 'перехода', 'переходов')} · {per(w.orders)} за 7 дней</span>;
  if (t.people) return <span className="an-mk-act warn">за 7 дней переходов не было</span>;
  return <span className="an-mk-act an-muted">переходов пока не было</span>;
}

function QrImage({ link, entry, size = 900 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    setSrc(null);
    api.getMarketingQrUrl(link.id, entry, size).then((u) => { url = u; setSrc(u); }).catch(() => setSrc(''));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [link.id, entry, size]);
  return (
    <div className="an-mk-qr">
      {src ? <img src={src} alt={`QR-код: ${ENTRY[entry]}`} /> : <div className="an-mk-qr-empty an-muted">{src === '' ? 'Не получилось' : 'Готовим…'}</div>}
      {src && <a className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }} href={src} download={`qr-${link.slug}-${entry}.png`}>Скачать PNG</a>}
    </div>
  );
}

// --- Расходы (этап 3): у любого места и своей ссылки; Директ — сам или вручную.
// Форма — общая с «Финансы → Затраты» (ExpenseForm, mode='marketing') ---
// Переход в «Финансы → Затраты» — для блоков расхода на любой глубине
const GoFinance = React.createContext(null);

// Строки — те же, что в «Финансах → Затраты» (правило и состояние месяца): одна запись, два окна (14.10)
function ExpenseBlock({ keys, expenses, onExpense, empty }) {
  const onGoFinance = React.useContext(GoFinance);
  const items = keys.flatMap(k => (expenses?.[k.key] || []).map(e => ({ ...e, keyTitle: keys.length > 1 ? k.title : '' })));
  return (
    <div className="an-mk-exp">
      <div className="an-mk-label">Расход</div>
      {items.length ? (
        <ul className="an-mk-exp-list">
          {items.map(e => (
            <li key={e.id}>
              <span>{e.keyTitle && <b>{e.keyTitle}: </b>}{e.label}{e.note ? <span className="an-muted"> · {e.note}</span> : null}</span>
              {e.month_state && <span className="an-muted an-small">{e.month_state}</span>}
              <button type="button" className="an-link" onClick={() => onExpense({ keys, item: e })}>Изменить</button>
            </li>
          ))}
        </ul>
      ) : <div className="an-muted an-small">{empty || 'Если за это место платите — продвижение, блогер, печать, — добавьте расход: отчёт посчитает цену заказа.'}</div>}
      <div className="an-mk-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onExpense({ keys })}>+ Добавить расход</button>
        {items.length > 0 && onGoFinance && (
          <button type="button" className="an-link" onClick={onGoFinance}>План и факт — в «Финансах → Затраты» →</button>
        )}
      </div>
    </div>
  );
}

// Внутри места или своей ссылки: все ссылки сразу (сайт / Telegram / Max) → куда вставить → расход (14.10)
function LinkPanel({ link, onQr, showToast, expenses, onExpense, children, extra }) {
  const entries = entriesOf(link);
  const sendText = [link.title, ...(link.steps || []).map((x, i) => `${i + 1}. ${x}`),
    ...entries.map(k => `${ENTRY[k]}: ${link.urls[k]}`)].join('\n');
  return (
    <div className="an-mk-panel">
      <div className="an-mk-main">
        <span className="an-mk-label">Ссылки</span>
        {entries.map(k => (
          <div key={k} className="an-mk-link-row">
            <span className="an-muted an-small">{ENTRY[k]}</span>
            {/* без «https://» — короче и читается; копируется полная ссылка */}
            <input className="form-input" readOnly value={link.urls[k].replace(/^https?:\/\//, '')} title={link.urls[k]}
              onFocus={(e) => e.target.select()} aria-label={`Ссылка: ${ENTRY[k]}`} />
            <button type="button" className="btn btn-primary btn-sm" onClick={() => copyText(link.urls[k], showToast)}>Копировать</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => onQr(link, k)} aria-label={`QR-код: ${ENTRY[k]}`}>QR</button>
          </div>
        ))}
      </div>
      <div className="an-mk-steps">
        {link.steps?.length > 0 && (<>
          <div className="an-mk-label">{link.qr ? 'Что сделать' : 'Куда вставить'}</div>
          <ol>{link.steps.map((x, i) => <li key={i}>{x}</li>)}</ol>
        </>)}
        <div className="an-mk-actions">
          <button type="button" className="an-link" onClick={() => copyText(sendText, showToast)}>Скопировать инструкцию со ссылками</button>
          {link.total?.people > 0 && <span className="an-muted an-small">всего {plural(link.total.people, 'переход', 'перехода', 'переходов')} · {per(link.total.orders)}</span>}
        </div>
        {children}
      </div>
      {extra}
      {onExpense && <ExpenseBlock keys={[{ key: link.key, title: link.title }]} expenses={expenses} onExpense={onExpense} />}
    </div>
  );
}

// «Яндекс Директ»: метки для объявлений (Поиск / РСЯ) и расход — сам из Директа или вручную
const DIRECT_KEYS = [{ key: 'direct:direct', title: 'Яндекс Директ' }];   // вручную — одной строкой, как в отчёте
// Куда отнести расход кампании — только строки Директа и реклама в каналах (владелец 28.10: «зачем там
// Threads, Карты, 2ГИС»); выбранная раньше другая строка в списке остаётся, чтобы её было видно
const DIRECT_TARGETS = [{ key: 'direct:direct_search', title: 'Поиск' }, { key: 'direct:direct_rsya', title: 'РСЯ' }];
function campaignTargets(targets, adLinks, current) {
  const ad = new Set(['place:tg_ads', 'place:max_ads', ...Object.values(adLinks || {}).flat().map(l => l.key)]);
  const list = [...DIRECT_TARGETS, ...targets.filter(t => ad.has(t.key))];
  const cur = current && !list.some(t => t.key === current) && targets.find(t => t.key === current);
  return cur ? [...list, cur] : list;
}
const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
};

function DirectPanel({ direct, targets, adLinks, expenses, onExpense, onTarget, onSync, showToast }) {
  const [busy, setBusy] = useState(false);
  // Строку отчёта у кампании меняют редко (кампания в каналах без «ТГ»/«MAX» в названии) — список
  // только для чтения, выбор — по «изменить» (владелец 28.10: MAX случайно ушла в «Яндекс Карты»)
  const [editing, setEditing] = useState(null);
  const sync = async () => { setBusy(true); try { await onSync(); } finally { setBusy(false); } };
  const rowTitle = (c) => (c.target_key
    ? ([...DIRECT_TARGETS, ...targets].find(t => t.key === c.target_key)?.title || 'другая строка')
    : (c.auto_title || 'по месту показа'));
  return (
    <div className="an-mk-panel">
      {direct.connected ? (
        <div className="an-mk-exp">
          <div className="an-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
            <span><span className="an-mk-label">Расход</span>{' '}
              <span className="an-muted an-small">подтягивается из Директа сам, раз в 6 часов{direct.last_sync ? ` · обновлено ${fmtWhen(direct.last_sync)}` : ' · ещё не загружался'}</span></span>
            <button type="button" className="an-link" disabled={busy} onClick={sync}>{busy ? 'Загружаем…' : 'Обновить сейчас'}</button>
          </div>
          {direct.error && <div className="an-small an-mk-warn">Последняя загрузка не удалась: {direct.error.replace(/^\S+\s/, '')}</div>}
          {direct.campaigns.length ? (
            <div className="an-scroll-x">
              <table className="an-table">
                <thead><tr><th>Кампания</th><th className="r">7 дней</th><th className="r">30 дней</th><th>В отчёте — строка</th></tr></thead>
                <tbody>
                  {direct.campaigns.map(c => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td className="r">{c.cost7 ? fmtRub(c.cost7) : '—'}</td>
                      <td className="r">{fmtRub(c.cost30)}</td>
                      <td>
                        {editing === c.id ? (
                          <span className="an-mk-edit">
                            <Select value={c.target_key || ''} onChange={(v) => { if (v !== (c.target_key || '')) onTarget(c.id, v); setEditing(null); }} options={[
                              { value: '', label: `Сама: ${c.auto_title || 'по месту показа'}` },
                              ...campaignTargets(targets, adLinks, c.target_key).map(t => ({ value: t.key, label: t.title })),
                            ]} />
                            <button type="button" className="an-link an-small" onClick={() => setEditing(null)}>отмена</button>
                          </span>
                        ) : (
                          <span>{rowTitle(c)}{c.target_key && <span className="an-muted an-small"> · выбрано вручную</span>}{' '}
                            <button type="button" className="an-link an-small" onClick={() => setEditing(c.id)}>изменить</button></span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="an-muted an-small">За 30 дней расхода в Директе нет.</div>}
        </div>
      ) : (
        <ExpenseBlock keys={DIRECT_KEYS} expenses={expenses} onExpense={onExpense}
          empty="Расход сам не подтягивается: Директ не подключён. Внесите расход вручную или попросите подключить Директ — @novaflo_support." />
      )}
      {/* Метки нужны один раз — при запуске кампании в поиске или РСЯ; каждый день на них смотреть незачем */}
      <details className="an-mk-howto">
        <summary>Запускаете новую кампанию в поиске или РСЯ?</summary>
        <div className="an-mk-howto-body">
          <span className="an-mk-label">Метки для объявлений</span>
          <div className="an-mk-url">
            <input className="form-input" readOnly value={direct.params} onFocus={(e) => e.target.select()} aria-label="Параметры URL" />
            <button type="button" className="btn btn-primary btn-sm" onClick={() => copyText(direct.params, showToast)}>Копировать</button>
          </div>
          <div className="an-mk-label">Куда вставить</div>
          <ol>
            <li>Директ → кампания → «Параметры кампании» → «Параметры URL».</li>
            <li>Вставьте строку как есть — одну и ту же во все кампании на поиске и в РСЯ. Скобки не заменяйте: при каждом клике Директ сам подставит, где было показано объявление, и заказы разделятся на «Поиск» и «РСЯ».</li>
            <li>Для рекламы в каналах Telegram и Max эта строка не нужна — там ссылки из «Директ · Telegram» и «Директ · Max».</li>
          </ol>
        </div>
      </details>
    </div>
  );
}

const AD_PLACES = ['tg_ads', 'max_ads'];
const fmtDM = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '');

/** «Реклама в Telegram / Max»: ссылки отдельных объявлений + объявления из Директа с выбором ссылки */
function AdBlock({ place, links, ads, connected, onAdTarget, onNew, onEdit, onArchive, showToast }) {
  const entry = place === 'max_ads' ? 'max' : 'tg';
  // «Основная ссылка» — явным ключом: пустое значение вернуло бы выбор кампании, а он может быть другим
  const mainKey = `place:${place}`;
  const options = [
    { value: mainKey, label: 'Основная ссылка' },
    ...links.map(l => ({ value: l.key, label: l.title })),
    { value: `nolink:${place}`, label: 'Без нашей ссылки — заказы не видны' },
  ];
  // Выбранная ссылка ушла в архив — не теряем её в списке
  const optionsFor = (key) => (key && !options.some(o => o.value === key) ? [...options, { value: key, label: 'Ссылка из архива' }] : options);
  return (
    <div className="an-mk-ads">
      <div className="an-mk-label">Ссылки для отдельных объявлений</div>
      {links.length ? (
        <ul className="an-mk-exp-list">
          {links.map(l => (
            <li key={l.id}>
              <span><b>{l.title}</b> <span className="an-muted an-small">{(l.urls[entry] || '').replace(/^https?:\/\//, '')}</span></span>
              {l.total?.people > 0 && <span className="an-muted an-small">{plural(l.total.people, 'переход', 'перехода', 'переходов')} · {per(l.total.orders)}</span>}
              <span className="an-mk-actions">
                {l.urls[entry] && <button type="button" className="an-link" onClick={() => copyText(l.urls[entry], showToast)}>Копировать</button>}
                <button type="button" className="an-link" onClick={() => onEdit(l)}>Изменить</button>
                <button type="button" className="an-link" onClick={() => onArchive(l, true)}>В архив</button>
              </span>
            </li>
          ))}
        </ul>
      ) : <div className="an-muted an-small">Чтобы видеть заказы по каждому объявлению, дайте каждому свою ссылку.</div>}
      <div className="an-mk-actions"><button type="button" className="btn btn-secondary btn-sm" onClick={onNew}>+ Ссылка для объявления</button></div>
      {(connected || ads.length > 0) && (<>
        <div className="an-mk-label" style={{ marginTop: 10 }}>Объявления из Директа</div>
        {ads.length ? (<>
          <div className="an-muted an-small">Директ не сообщает, какая ссылка стоит в объявлении в каналах, — выберите её сами. Тогда расход объявления встанет рядом с его переходами и заказами.</div>
          <div className="an-scroll-x">
            <table className="an-table">
              <thead><tr><th>Объявление</th><th className="r">Расход</th><th className="r">Клики</th><th>Ссылка в объявлении</th></tr></thead>
              <tbody>
                {ads.map(a => (
                  <tr key={a.id}>
                    <td>с {fmtDM(a.from)} по {fmtDM(a.to)}<div className="an-muted an-small">кампания «{a.campaign}» · № {a.id}</div></td>
                    <td className="r">{fmtRub(a.cost)}</td>
                    <td className="r">{fmtNum(a.clicks)}</td>
                    <td><Select value={a.target_key || mainKey} onChange={(v) => onAdTarget(a.id, v)} options={optionsFor(a.target_key)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>) : <div className="an-muted an-small">За 90 дней объявлений в Директе нет.</div>}
      </>)}
    </div>
  );
}

// Продвижение, внесённое в «Финансах» без места: отнести к месту целиком или частью суммы (13.10)
function PlaceList({ places, direct, adLinks, targets, onQr, showToast, expenses, onExpense, onTarget, onSync, onAdTarget, onNewAdLink, onEditLink, onArchive }) {
  const [open, setOpen] = useState(null);
  const d7 = direct ? Object.values(direct.spend7 || {}).reduce((sum, v) => sum + v, 0) : 0;
  return (
    <section className="card an-mk-list" style={{ '--ch': '12px' }}>
      {direct && (
        <div className={`an-mk-item${open === 'direct' ? ' open' : ''}`}>
          <button type="button" className="an-mk-item-head" aria-expanded={open === 'direct'} onClick={() => setOpen(open === 'direct' ? null : 'direct')}>
            <span className="an-caret" aria-hidden="true">{open === 'direct' ? '▾' : '▸'}</span>
            <b>Яндекс Директ</b>
            <span className="an-muted an-small an-mk-where">Поиск и РСЯ</span>
            <span className={`an-mk-act${direct.connected ? '' : ' an-muted'}`}>{direct.connected ? (d7 ? `расход ${fmtRub(d7)} за 7 дней` : 'расхода за 7 дней нет') : 'расход — вручную'}</span>
          </button>
          {open === 'direct' && <DirectPanel direct={direct} targets={targets} adLinks={adLinks} expenses={expenses} onExpense={onExpense} onTarget={onTarget} onSync={onSync} showToast={showToast} />}
        </div>
      )}
      {places.map(l => {
        const on = open === l.id;
        return (
          <div key={l.id} className={`an-mk-item${on ? ' open' : ''}`}>
            <button type="button" className="an-mk-item-head" aria-expanded={on} onClick={() => setOpen(on ? null : l.id)}>
              <span className="an-caret" aria-hidden="true">{on ? '▾' : '▸'}</span>
              <b>{l.title}</b>
              <span className="an-muted an-small an-mk-where">{l.where}</span>
              <Activity link={l} />
            </button>
            {on && (
              <LinkPanel link={l} onQr={onQr} showToast={showToast} expenses={expenses} onExpense={onExpense}
                extra={AD_PLACES.includes(l.place) && (
                  <AdBlock place={l.place} links={adLinks?.[l.place] || []} ads={(direct?.ads || []).filter(a => a.place === l.place)}
                    connected={!!direct?.connected} onAdTarget={onAdTarget} onNew={() => onNewAdLink(l.place)} onEdit={onEditLink}
                    onArchive={onArchive} showToast={showToast} />
                )} />
            )}
          </div>
        );
      })}
    </section>
  );
}

const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');

function LinkForm({ link, place, onSave, onClose }) {
  const forAd = AD_PLACES.includes(link?.place || place);   // ссылка объявления в мессенджере — ведёт в бота
  const [title, setTitle] = useState(link?.title || '');
  const [hasPeriod, setHasPeriod] = useState(!!(link?.starts_on || link?.ends_on));
  const [from, setFrom] = useState(link?.starts_on || '');
  const [to, setTo] = useState(link?.ends_on || '');
  const [target, setTarget] = useState(link?.target || '');
  // Без ссылки — только бюджет: строка в отчёте с расходом, ссылку можно выдать потом
  const withLink = link ? link.link_enabled !== false : true;   // новые — только со ссылкой (14.10)
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({ title, target, starts_on: hasPeriod ? from : '', ends_on: hasPeriod ? to : '', link_enabled: withLink });
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2 className="modal-title">{link ? (link.link_enabled === false ? 'Изменить строку' : 'Изменить ссылку') : forAd ? 'Ссылка для объявления' : 'Новая ссылка'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button></div>
        <div className="modal-body an-mk-form">
          <label htmlFor="mk-title">Название</label>
          <input id="mk-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={forAd ? 'Объявление «Экзотика охапками»' : 'Листовки в ТЦ «Галерея»'} autoFocus required />
          <div className="an-muted an-small">Так строка будет называться в отчёте</div>
          {link && link.link_enabled === false && (
            <div className="an-muted an-small">Строка без ссылки (заведена раньше). Новые расходы без ссылки вносите в «Финансах» как общий маркетинг.</div>
          )}
          {withLink && !forAd && (<>
            <label htmlFor="mk-target">Куда ведёт на сайте</label>
            <input id="mk-target" className="form-input" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="на главную — оставьте пустым; или /catalog/3-rozy/" />
          </>)}
          <span className="an-mk-label">Когда работает</span>
          <div className="an-mk-radio">
            <label><input type="radio" checked={!hasPeriod} onChange={() => setHasPeriod(false)} /> Без срока</label>
            <label><input type="radio" checked={hasPeriod} onChange={() => setHasPeriod(true)} /> С
              <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} disabled={!hasPeriod} aria-label="Начало" />
              по <input type="date" className="form-input" value={to} onChange={(e) => setTo(e.target.value)} disabled={!hasPeriod} aria-label="Конец" /></label>
          </div>
          {withLink && <div className="an-muted an-small">После окончания ссылка не сломается: кто придёт по ней позже, попадёт в магазин, и заказ засчитается ей.</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !title.trim()}>{busy ? 'Сохраняем…' : link ? 'Сохранить' : 'Создать'}</button>
        </div>
      </form>
    </div>
  );
}

function QrModal({ link, entry, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2 className="modal-title">QR-код · {link.title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button></div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          <div>{ENTRY_TO[entry]}</div>
          <QrImage link={link} entry={entry} />
          <code className="an-small" style={{ wordBreak: 'break-all', textAlign: 'center' }}>{link.urls[entry]}</code>
          <div className="an-muted an-small">Для печати — не меньше 2 × 2 см, рядом — «Заказать букет».</div>
        </div>
      </div>
    </div>
  );
}

function ShareModal({ onClose, showToast }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getMarketingShare().then(setS).catch(e => showToast?.(e.message, 'error')); }, [showToast]);
  const run = async (fn) => {
    setBusy(true);
    try { setS(await fn()); } catch (e) { showToast?.(e.message, 'error'); } finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2 className="modal-title">Поделиться всеми ссылками</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button></div>
        <div className="modal-body an-mk-form">
          {!s ? <div className="an-muted">Загрузка…</div> : (<>
            <div className="an-muted an-small">Отправьте тому, кто будет вставлять ссылки: владельцу, администратору или тому, кто ведёт соцсети. Входить в админку не нужно.</div>
            <span className="an-mk-label">Страница со всеми ссылками и QR-кодами</span>
            {s.url ? (
              <div className="an-mk-row"><code>{s.url.replace(/^https:\/\//, '')}</code>
                <button type="button" className="an-link" onClick={() => copyText(s.url, showToast)}>Копировать</button>
                <a className="an-link" href={s.url} target="_blank" rel="noreferrer">Открыть</a></div>
            ) : (
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => run(() => api.createMarketingShare())}>Создать страницу</button>
            )}
            <span className="an-mk-label">Сообщение для мессенджера</span>
            <textarea className="form-input" readOnly rows={8} value={s.message} style={{ fontSize: 13 }} />
            <button type="button" className="btn btn-primary btn-sm" onClick={() => copyText(s.message, showToast)}>Копировать текст</button>
            {s.url && (
              <div className="an-muted an-small">Страница работает, пока вы её не отключите. На ней нет цифр — только ссылки, QR и подсказки.{' '}
                <button type="button" className="an-link" disabled={busy} onClick={() => run(() => api.disableMarketingShare())}>Отключить</button></div>
            )}
          </>)}
        </div>
      </div>
    </div>
  );
}

/** Строка без ссылки: только расход; «Выдать ссылку» включает обычную ссылку */
function NoLinkPanel({ link, onEnable, expenses, onExpense, children }) {
  const [busy, setBusy] = useState(false);
  const enable = async () => { setBusy(true); try { await onEnable(link); } finally { setBusy(false); } };
  return (
    <div className="an-mk-panel">
      <div className="an-mk-main">
        <div className="an-muted">Ссылки нет — заказы к этой строке не привязываются, в отчёте будет только расход.</div>
        {onEnable && <div><button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={enable}>{busy ? 'Выдаём…' : 'Выдать ссылку'}</button></div>}
      </div>
      <div className="an-mk-steps">{children}</div>
      {onExpense && <ExpenseBlock keys={[{ key: link.key, title: link.title }]} expenses={expenses} onExpense={onExpense} />}
    </div>
  );
}

function CustomTable({ title, items, onEdit, onQr, onArchive, onEnable, showToast, muted, expenses, onExpense }) {
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  return (
    <>
      {title && <div className="an-subtitle">{title}</div>}
      <div className="an-scroll-x">
        <table className="an-table an-table-click">
          <thead><tr><th>Название</th><th>Когда работает</th><th className="r">За 7 дней</th><th className="r">Всего</th></tr></thead>
          <tbody>
            {items.map(l => {
              const noLink = l.link_enabled === false;
              const actions = (
                <div className="an-mk-actions">
                  <button type="button" className="an-link" onClick={() => onEdit(l)}>Изменить</button>
                  <button type="button" className="an-link" onClick={() => onArchive(l, l.status !== 'archived')}>{l.status === 'archived' ? 'Вернуть' : 'В архив'}</button>
                </div>
              );
              return (
              <React.Fragment key={l.id}>
                <tr className={`${open === l.id ? 'sel' : ''}${muted ? ' an-row-muted' : ''}`} onClick={() => setOpen(o => (o === l.id ? null : l.id))} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setOpen(o => (o === l.id ? null : l.id)); }}>
                  <td><span className="an-caret" aria-hidden="true">{open === l.id ? '▾' : '▸'}</span><b>{l.title}</b>
                    {noLink && <div className="an-muted an-small">без ссылки</div>}</td>
                  <td>{l.starts_on || l.ends_on ? `${fmtD(l.starts_on) || '…'} — ${fmtD(l.ends_on) || '…'}` : 'без срока'}</td>
                  <td className="r">{!noLink && l.week?.people ? `${fmtNum(l.week.people)} · ${per(l.week.orders)}` : '—'}</td>
                  <td className="r">{!noLink && l.total?.people ? `${fmtNum(l.total.people)} · ${per(l.total.orders)}` : '—'}</td>
                </tr>
                {open === l.id && (
                  <tr className="an-expand an-mk-expand"><td colSpan={4}>
                    {noLink
                      ? <NoLinkPanel link={l} onEnable={onEnable} expenses={expenses} onExpense={onExpense}>{actions}</NoLinkPanel>
                      : <LinkPanel link={l} onQr={onQr} showToast={showToast} expenses={expenses} onExpense={onExpense}>{actions}</LinkPanel>}
                  </td></tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function MarketingLinks({ showToast, onGoFinance }) {
  const [data, setData] = useState(null);
  const [qr, setQr] = useState(null);           // {link, entry}
  const [expForm, setExpForm] = useState(null);  // {keys, item?}
  const openQr = (link, entry) => setQr({ link, entry });
  const [form, setForm] = useState(null);       // null | {} (новая) | link
  const [share, setShare] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const load = useCallback(() => api.getMarketingLinks().then(setData).catch(e => showToast?.(e.message, 'error')), [showToast]);
  useEffect(() => { load(); }, [load]);
  const save = async (values) => {
    try {
      const res = form && form.id ? await api.updateMarketingLink(form.id, values) : await api.createMarketingLink({ ...values, place: form?.place });
      setData(res);
      showToast?.(form && form.id ? 'Сохранено' : 'Ссылка готова');
    } catch (e) {
      showToast?.(e.message, 'error');
      throw e;
    }
  };
  const call = async (fn, ok) => {
    try {
      setData(await fn());
      if (ok) showToast?.(ok);
    } catch (e) {
      showToast?.(e.message, 'error');
      throw e;
    }
  };
  const saveExpense = (item, values) => call(() => (item ? api.updateMarketingExpense(item.id, values) : api.createMarketingExpense(values)), 'Расход сохранён');
  const deleteExpense = (item) => call(() => api.deleteMarketingExpense(item.id), 'Расход удалён');
  const setTarget = (cid, key) => call(() => api.setDirectCampaignTarget(cid, key), 'Сохранено').catch(() => {});
  const syncDirect = () => call(() => api.syncDirectSpend(), 'Расход Директа обновлён').catch(() => {});
  const setAdTarget = (adId, key) => call(() => api.setDirectAdTarget(adId, key), 'Сохранено').catch(() => {});
  const archive = async (l, on) => {
    try { setData(await api.updateMarketingLink(l.id, { archived: on })); } catch (e) { showToast?.(e.message, 'error'); }
  };
  // «Выдать ссылку» строке без ссылки
  const enableLink = (l) => call(() => api.updateMarketingLink(l.id, { link_enabled: true }), 'Ссылка готова').catch(() => {});
  if (!data) return <div className="an-muted" style={{ padding: 24 }}>Загрузка…</div>;
  const noEntries = !data.entries.max ? 'Max-бота у магазина пока нет — ссылки только на сайт и в Telegram.' : '';
  return (
    <GoFinance.Provider value={onGoFinance || null}>
    <div className="an-stack">
      <div className="an-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <span className="an-muted">Откройте место — там ссылка и куда её вставить. Заказы оттуда появятся в отчёте.{noEntries ? ` ${noEntries}` : ''}</span>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setShare(true)}>Поделиться всеми</button>
      </div>
      <PlaceList places={data.places} direct={data.direct} adLinks={data.ad_links} targets={data.targets} onQr={openQr} showToast={showToast}
        expenses={data.expenses} onExpense={setExpForm} onTarget={setTarget} onSync={syncDirect} onAdTarget={setAdTarget}
        onNewAdLink={(place) => setForm({ place })} onEditLink={setForm} onArchive={archive} />
      <Panel title="Свои ссылки" aside={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm({})}>+ Добавить</button>}>
        <div className="an-muted an-small" style={{ marginBottom: 8 }}>Для листовок, блогеров и акций — каждая своей строкой в отчёте. Расход без ссылки (блогер, радио, подарки) вносите в «Финансах» как общий маркетинг.</div>
        {data.custom.length ? (
          <CustomTable items={data.custom} onEdit={setForm} onQr={openQr} onArchive={archive} onEnable={enableLink} showToast={showToast} expenses={data.expenses} onExpense={setExpForm} />
        ) : <Empty>Своих ссылок пока нет</Empty>}
        {(data.ended.length > 0 || data.archived.length > 0) && (
          <button type="button" className="an-link" style={{ marginTop: 10 }} onClick={() => setShowOld(v => !v)}>
            {showOld ? 'Скрыть' : [data.ended.length ? `Закончились (${data.ended.length})` : '', data.archived.length ? `Архив (${data.archived.length})` : ''].filter(Boolean).join(' · ')}
          </button>
        )}
        {showOld && <>
          <CustomTable title="Закончились" items={data.ended} onEdit={setForm} onQr={openQr} onArchive={archive} onEnable={enableLink} showToast={showToast} expenses={data.expenses} onExpense={setExpForm} />
          <CustomTable title="Архив" items={data.archived} onEdit={setForm} onQr={openQr} onArchive={archive} showToast={showToast} muted />
        </>}
      </Panel>
      {qr && <QrModal link={qr.link} entry={qr.entry} onClose={() => setQr(null)} />}
      {form && <LinkForm link={form.id ? form : null} place={form.place} onSave={save} onClose={() => setForm(null)} />}
      {share && <ShareModal onClose={() => setShare(false)} showToast={showToast} />}
      {expForm && <ExpenseForm mode="marketing" keys={expForm.keys} item={expForm.item} targets={data.targets} onSave={saveExpense} onDelete={deleteExpense} onClose={() => setExpForm(null)} />}
    </div>
    </GoFinance.Provider>
  );
}
